import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useCreateGroupList } from '../../../../sic-lib/api/hooks/useGroupsList';
import { DEFAULT_VIEW_ID } from '../../../../sic-lib/defaults';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { buildCreateList, ListType } from '../../../../sic-lib/types/Lists';
import { IErrorFormValidation } from '../../../../sic-lib/utils/formValidationHelpers';
import ButtonLoading from '../../../Shared/ButtonLoading';

const useStyles = makeStyles()((theme: Theme) => ({
    paper: {
        width: '100%',
    },
    text: {
        marginBottom: theme.spacing(3),
    },
}));

interface DialogState {
    title: string;
    formError: IErrorFormValidation | null;
}

interface Props {
    isOpen: boolean;
    contactIds: number[];
    onDoneHandler: (result?: boolean) => void;
}

export default function DialogCreateGroupedContacts({ isOpen, contactIds, onDoneHandler }: Props) {
    const { classes } = useStyles();
    const { mutate: createGroupListMutation, isPending: isLoading } = useCreateGroupList();
    const [dialogState, dialogDispatch] = React.useReducer<GenericReducer<DialogState>>(
        genericReducer,
        {
            title: '',
            formError: null,
        },
    );

    const handleChangeTitle = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        dialogDispatch({ title: event.target.value });
    };

    const handleCancel = () => {
        onDoneHandler();
    };

    const handleSubmit = async () => {
        try {
            const list = buildCreateList({
                name: dialogState.title,
                type: ListType.static,
                viewId: DEFAULT_VIEW_ID,
                items: contactIds,
            });

            createGroupListMutation(list);

            onDoneHandler(true);
        } catch (error: any) {
            if (error.isFormError) {
                dialogDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <Dialog
            maxWidth="xs"
            aria-labelledby="dialog_remove_group_contact"
            open={isOpen}
            PaperProps={{
                className: classes.paper,
            }}
        >
            <DialogTitle id="dialog_remove_group_contact">Create Contacts Group</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body1" className={classes.text}>
                    Enter a title for the new contact group
                </Typography>
                <TextField
                    id="contact_group_title"
                    label="Title"
                    name="title"
                    value={dialogState.title}
                    onChange={handleChangeTitle}
                    autoFocus
                    fullWidth
                    required
                    error={Boolean(dialogState.formError?.field === 'name')}
                    helperText={
                        dialogState.formError?.field === 'name' ? dialogState.formError.message : ''
                    }
                ></TextField>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <ButtonLoading
                    isLoading={isLoading}
                    onClick={handleSubmit}
                    color="secondary"
                    variant="outlined"
                >
                    Ok
                </ButtonLoading>
            </DialogActions>
        </Dialog>
    );
}
